import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CalendarDays, ChevronLeft, ChevronRight, Plus, X, Trash2, MapPin, Settings2, Pencil, Repeat } from "lucide-react";

const WEEKDAYS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
const WD_SHORT = ["L", "M", "X", "J", "V", "S", "D"];

function monthRange(month) {
  const [y, m] = month.split("-").map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return [`${month}-01`, `${month}-${String(last).padStart(2, "0")}`];
}

export default function ClubCalendarPage() {
  const { user } = useAuth();
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [filterTeam, setFilterTeam] = useState("");
  const [openDate, setOpenDate] = useState(null);
  const [editEvent, setEditEvent] = useState(null); // null | {} nuevo | evento
  const [showConfig, setShowConfig] = useState(false);
  const [showCopy, setShowCopy] = useState(false);

  const load = async () => {
    const [from, to] = monthRange(month);
    try {
      const r = await api.get(`/calendar?date_from=${from}&date_to=${to}${filterTeam ? `&team_id=${filterTeam}` : ""}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [month, filterTeam]);

  const eventsByDate = useMemo(() => {
    const m = {};
    (data?.events || []).forEach(ev => { (m[ev.date] = m[ev.date] || []).push(ev); });
    return m;
  }, [data]);

  const daysGrid = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const first = new Date(Date.UTC(y, m - 1, 1));
    const startOffset = (first.getUTCDay() + 6) % 7;
    const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const grid = [];
    for (let i = 0; i < startOffset; i++) grid.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const iso = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      grid.push({ day: d, iso, events: eventsByDate[iso] || [] });
    }
    while (grid.length % 7 !== 0) grid.push(null);
    return grid;
  }, [month, eventsByDate]);

  const shiftMonth = (delta) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(Date.UTC(y, m - 1 + delta, 1));
    setMonth(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`);
  };

  const monthLabel = new Date(month + "-01").toLocaleDateString("es-ES", { month: "long", year: "numeric" });
  const canManage = data?.can_manage;
  const manageableIds = data?.manageable_team_ids || [];

  return (
    <div>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="overline flex items-center gap-2"><CalendarDays size={12} /> Calendario del club</div>
          <h1 className="font-heading font-black text-5xl tracking-tighter uppercase mt-2">Calendario</h1>
          <p className="text-muted-foreground text-sm mt-2">Entrenos, partidos y campos del club — cada equipo con su color.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select data-testid="cal-team-filter" value={filterTeam} onChange={(e) => setFilterTeam(e.target.value)} className="h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none">
            <option value="">Todos los equipos</option>
            {(data?.teams || []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <button data-testid="cal-config-btn" onClick={() => setShowConfig(s => !s)} className={`h-10 px-4 border-2 inline-flex items-center gap-2 font-heading font-black uppercase text-xs tracking-wider ${showConfig ? "border-primary text-primary bg-primary/10" : "border-border hover:border-primary/60"}`}>
            <Settings2 size={14} /> Configurar
          </button>
          {canManage && (
            <button data-testid="cal-new-event-btn" onClick={() => setEditEvent({ team_id: manageableIds[0] || data?.teams?.[0]?.id || "", type: "entreno", date: new Date().toISOString().slice(0, 10), time: "", convocados: [] })} className="btn-volt h-10">
              <Plus size={15} /> Evento
            </button>
          )}
        </div>
      </div>

      {showConfig && data && <ConfigPanel data={data} user={user} reload={load} />}

      <div className="border border-border">
        <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
          <div className="font-heading font-black text-2xl uppercase tracking-tight capitalize">{monthLabel}</div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="hidden md:flex items-center gap-3 mr-3">
              {(data?.teams || []).slice(0, 6).map(t => (
                <span key={t.id} className="flex items-center gap-1.5 text-[10px] font-mono text-muted-foreground">
                  <span className="w-2.5 h-2.5 inline-block" style={{ background: t.color }} /> {t.name}
                </span>
              ))}
            </div>
            <button data-testid="cal-copy-week" onClick={() => setShowCopy(true)} className="h-9 px-3 border border-border hover:border-primary text-[10px] font-heading uppercase tracking-widest">Copiar semana</button>
            <button data-testid="cal-prev" onClick={() => shiftMonth(-1)} className="h-9 w-9 border border-border hover:border-primary flex items-center justify-center"><ChevronLeft size={15} /></button>
            <button data-testid="cal-today" onClick={() => setMonth(new Date().toISOString().slice(0, 7))} className="h-9 px-3 border border-border hover:border-primary text-[10px] font-heading uppercase tracking-widest">Hoy</button>
            <button data-testid="cal-next" onClick={() => shiftMonth(1)} className="h-9 w-9 border border-border hover:border-primary flex items-center justify-center"><ChevronRight size={15} /></button>
          </div>
        </div>
        <div className="p-3">
          <div className="grid grid-cols-7 gap-1 text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-2">
            {WD_SHORT.map(d => <div key={d} className="text-center py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1" data-testid="cal-grid">
            {daysGrid.map((cell, i) => {
              if (!cell) return <div key={i} className="min-h-[72px]" />;
              const isToday = cell.iso === new Date().toISOString().slice(0, 10);
              return (
                <button key={i} type="button" data-testid={`cal-day-${cell.iso}`} onClick={() => setOpenDate(cell.iso)}
                  className={`min-h-[72px] border p-1 flex flex-col items-stretch gap-0.5 text-left transition-colors ${cell.events.length ? "border-border hover:border-primary/60" : "border-border/50 hover:border-primary/40"} ${isToday ? "ring-1 ring-primary" : ""}`}>
                  <span className={`font-mono text-[11px] ${isToday ? "text-primary font-bold" : "text-muted-foreground"}`}>{cell.day}</span>
                  {cell.events.slice(0, 3).map(ev => (
                    <span key={ev.id} className="text-[8px] leading-tight font-heading uppercase truncate px-1 py-0.5 text-white" style={{ background: ev.color, opacity: ev.virtual ? 0.55 : 1 }}>
                      {ev.time ? `${ev.time} ` : ""}{ev.type === "partido" ? "⚽" : "●"} {ev.type === "partido" ? (ev.opponent || "Partido") : "Entreno"}
                    </span>
                  ))}
                  {cell.events.length > 3 && <span className="text-[8px] font-mono text-muted-foreground px-1">+{cell.events.length - 3} más</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {openDate && (
        <DayModal date={openDate} events={eventsByDate[openDate] || []} onClose={() => setOpenDate(null)}
          canManage={canManage} manageableIds={manageableIds}
          onAdd={() => { setEditEvent({ team_id: manageableIds[0] || data?.teams?.[0]?.id || "", type: "entreno", date: openDate, time: "", convocados: [] }); setOpenDate(null); }}
          onEdit={(ev) => { setEditEvent(ev); setOpenDate(null); }}
          onDeleted={load} />
      )}
      {editEvent && data && (
        <EventForm event={editEvent} data={data} manageableIds={manageableIds}
          onClose={() => setEditEvent(null)} onSaved={() => { setEditEvent(null); load(); }} />
      )}
      {showCopy && (
        <CopyWeekModal teams={data?.teams || []} onClose={() => setShowCopy(false)} onDone={() => { setShowCopy(false); load(); }} />
      )}
    </div>
  );
}

function CopyWeekModal({ teams, onClose, onDone }) {
  const monday = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x.toISOString().slice(0, 10); };
  const [teamId, setTeamId] = useState("");
  const [src, setSrc] = useState(() => monday(new Date()));
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [skips, setSkips] = useState([]);
  const [skipInput, setSkipInput] = useState("");
  const [saving, setSaving] = useState(false);

  const copy = async () => {
    if (!src || !from || !to) { toast.error("Completa las fechas"); return; }
    setSaving(true);
    try {
      const { data: r } = await api.post("/calendar/copy-week", {
        team_id: teamId || null, source_week_start: src, target_from: from, target_to: to,
        skip_dates: skips,
      });
      toast.success(`${r.created} entrenos creados`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background border-2 border-border max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="copy-week-modal">
        <div className="overline text-primary mb-1">Planificación por semanas</div>
        <h3 className="font-heading font-black text-2xl uppercase tracking-tight mb-1">Copiar semana</h3>
        <p className="text-xs text-muted-foreground mb-4">Replica los entrenos de una semana en el periodo que elijas (otra semana, un mes...). Se crean como entrenos editables uno a uno y no se duplican los que ya existan.</p>
        <div className="space-y-3">
          <div>
            <label className="overline block mb-1">Equipo</label>
            <select data-testid="copy-team" value={teamId} onChange={(e) => setTeamId(e.target.value)} className="w-full h-10 bg-card border border-border px-3 font-mono text-sm">
              <option value="">Todos los equipos</option>
              {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className="overline block mb-1">Semana origen (cualquier día de esa semana)</label>
            <input data-testid="copy-src" type="date" value={src} onChange={(e) => setSrc(e.target.value)} className="w-full h-10 bg-transparent border border-border px-3 font-mono text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1">Copiar desde</label>
              <input data-testid="copy-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-full h-10 bg-transparent border border-border px-3 font-mono text-sm" />
            </div>
            <div>
              <label className="overline block mb-1">Hasta</label>
              <input data-testid="copy-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-full h-10 bg-transparent border border-border px-3 font-mono text-sm" />
            </div>
          </div>
          <div>
            <label className="overline block mb-1">Festivos / vacaciones a saltar (opcional)</label>
            <div className="flex gap-2">
              <input data-testid="copy-skip-input" type="date" value={skipInput} onChange={(e) => setSkipInput(e.target.value)} className="flex-1 h-10 bg-transparent border border-border px-3 font-mono text-sm" />
              <button data-testid="copy-skip-add" type="button" onClick={() => { if (skipInput && !skips.includes(skipInput)) { setSkips([...skips, skipInput].sort()); setSkipInput(""); } }} className="h-10 px-3 border border-border hover:border-primary text-[10px] font-heading uppercase tracking-widest">Añadir</button>
            </div>
            {skips.length > 0 && (
              <div className="flex gap-1.5 flex-wrap mt-2" data-testid="copy-skip-chips">
                {skips.map((d) => (
                  <span key={d} className="inline-flex items-center gap-1 border border-amber-400/50 text-amber-400 px-2 py-0.5 font-mono text-[10px]">
                    {d}
                    <button onClick={() => setSkips(skips.filter((x) => x !== d))} className="hover:text-destructive">✕</button>
                  </span>
                ))}
              </div>
            )}
            <p className="text-[10px] text-muted-foreground mt-1">Esos días no se crearán entrenos aunque toquen por horario.</p>
          </div>
          <div className="flex gap-2 pt-1">
            <button data-testid="copy-submit" disabled={saving} onClick={copy} className="flex-1 h-11 bg-primary text-white font-heading font-black uppercase text-xs tracking-widest disabled:opacity-50">{saving ? "Copiando…" : "Copiar entrenos"}</button>
            <button onClick={onClose} className="h-11 px-4 border border-border font-heading uppercase text-xs tracking-widest">Cancelar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DayModal({ date: iso, events, onClose, canManage, manageableIds, onAdd, onEdit, onDeleted }) {
  const del = async (ev) => {
    if (!window.confirm("¿Eliminar este evento?")) return;
    try { await api.delete(`/calendar/events/${ev.id}`); toast.success("Evento eliminado"); onDeleted(); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-card border border-border w-full max-w-lg p-6 space-y-4 max-h-[85vh] overflow-y-auto" data-testid="cal-day-modal">
        <div className="flex items-start justify-between">
          <div className="font-heading font-black text-2xl uppercase tracking-tight capitalize">
            {new Date(iso + "T00:00:00").toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}
          </div>
          <button onClick={onClose} data-testid="cal-day-close" className="p-1 hover:text-primary"><X size={18} /></button>
        </div>
        {events.length === 0 && <div className="text-muted-foreground text-sm">Sin eventos este día.</div>}
        <div className="space-y-2">
          {events.map(ev => (
            <div key={ev.id} data-testid={`cal-event-${ev.id}`} className="border border-border p-3 flex items-start gap-3" style={{ borderLeft: `4px solid ${ev.color}` }}>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold text-sm uppercase flex items-center gap-2 flex-wrap">
                  {ev.type === "partido" ? `⚽ ${ev.opponent ? `vs ${ev.opponent}` : "Partido"}` : "Entreno"}
                  {ev.virtual && <span className="text-[9px] border border-border px-1.5 py-0.5 text-muted-foreground font-mono normal-case inline-flex items-center gap-1"><Repeat size={9} /> semanal</span>}
                  {ev.type === "partido" && ev.home_away && <span className="text-[9px] font-mono text-muted-foreground normal-case">({ev.home_away === "casa" ? "en casa" : "fuera"})</span>}
                </div>
                <div className="text-[11px] font-mono text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                  <span style={{ color: ev.color }}>■ {ev.team_name}</span>
                  {ev.time && <span>{ev.time}{ev.duration_min ? ` · ${ev.duration_min}'` : ""}</span>}
                  {ev.venue_name && <span className="inline-flex items-center gap-1"><MapPin size={10} /> {ev.venue_name}</span>}
                  {ev.type === "partido" && (ev.convocados?.length > 0) && <span>{ev.convocados.length} convocados</span>}
                </div>
                {ev.notes && <div className="text-xs text-muted-foreground mt-1">{ev.notes}</div>}
                {ev.announcement_id && <EventRsvps eventId={ev.id} />}
              </div>
              {!ev.virtual && canManage && manageableIds.includes(ev.team_id) && (
                <div className="flex gap-1">
                  <button data-testid={`cal-edit-${ev.id}`} onClick={() => onEdit(ev)} className="p-1.5 border border-border hover:border-primary"><Pencil size={12} /></button>
                  <button data-testid={`cal-del-${ev.id}`} onClick={() => del(ev)} className="p-1.5 border border-destructive/50 text-destructive hover:bg-destructive/10"><Trash2 size={12} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
        {canManage && (
          <button data-testid="cal-day-add" onClick={onAdd} className="btn-volt w-full h-10 justify-center"><Plus size={14} /> Añadir evento este día</button>
        )}
      </div>
    </div>
  );
}

function EventForm({ event, data, manageableIds, onClose, onSaved }) {
  const isNew = !event.id;
  const [f, setF] = useState({
    team_id: event.team_id || "", type: event.type || "entreno", date: event.date || "",
    time: event.time || "", duration_min: event.duration_min || "", venue_id: event.venue_id || "",
    opponent: event.opponent || "", home_away: event.home_away || "casa",
    convocados: event.convocados || [], notes: event.notes || "",
  });
  const [players, setPlayers] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));

  useEffect(() => {
    if (f.type !== "partido") return;
    api.get("/players").then(r => setPlayers(r.data)).catch(() => {});
  }, [f.type]);

  const teamPlayers = players.filter(p => p.team_id === f.team_id);
  const toggleConv = (pid) => set("convocados", f.convocados.includes(pid) ? f.convocados.filter(x => x !== pid) : [...f.convocados, pid]);

  const save = async (forceOverride = false) => {
    if (!f.team_id || !f.date) { toast.error("Equipo y fecha son obligatorios"); return; }
    setSaving(true);
    const payload = { ...f, notify_families: !!f.notify_families, force: forceOverride, duration_min: f.duration_min ? Number(f.duration_min) : null, venue_id: f.venue_id || null, opponent: f.opponent || null, notes: f.notes || null, home_away: f.type === "partido" ? f.home_away : null, convocados: f.type === "partido" ? f.convocados : [] };
    try {
      if (isNew) await api.post("/calendar/events", payload);
      else await api.put(`/calendar/events/${event.id}`, payload);
      toast.success(isNew ? "Evento creado" : "Evento actualizado");
      onSaved();
    } catch (e) {
      if (e.response?.status === 409) {
        const msg = e.response?.data?.detail || "El campo ya está ocupado a esa hora.";
        setSaving(false);
        if (window.confirm(`⚠️ CHOQUE DE CAMPO\n\n${msg}\n\n¿Quieres guardarlo igualmente?`)) {
          save(true);
        }
        return;
      }
      toast.error(formatApiError(e.response?.data?.detail));
    }
    finally { setSaving(false); }
  };

  const inputCls = "w-full h-10 bg-transparent border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none";
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-card border border-border w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto" data-testid="cal-event-form">
        <div className="flex items-start justify-between">
          <div className="font-heading font-black text-2xl uppercase tracking-tight">{isNew ? "Nuevo evento" : "Editar evento"}</div>
          <button onClick={onClose} data-testid="cal-form-close" className="p-1 hover:text-primary"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {["entreno", "partido"].map(t => (
            <button key={t} data-testid={`cal-type-${t}`} onClick={() => set("type", t)} className={`h-10 border-2 font-heading font-black uppercase text-xs tracking-wider ${f.type === t ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground"}`}>
              {t === "entreno" ? "Entreno" : "⚽ Partido"}
            </button>
          ))}
        </div>
        <div>
          <label className="overline block mb-1">Equipo</label>
          <select data-testid="cal-form-team" value={f.team_id} onChange={(e) => set("team_id", e.target.value)} className={inputCls}>
            {(data.teams || []).filter(t => manageableIds.includes(t.id)).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-1">
            <label className="overline block mb-1">Fecha</label>
            <input data-testid="cal-form-date" type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="overline block mb-1">Hora</label>
            <input data-testid="cal-form-time" type="time" value={f.time} onChange={(e) => set("time", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="overline block mb-1">Duración (min)</label>
            <input data-testid="cal-form-duration" type="number" min="0" value={f.duration_min} onChange={(e) => set("duration_min", e.target.value)} className={inputCls} />
          </div>
        </div>
        <div>
          <label className="overline block mb-1"><MapPin size={11} className="inline mb-0.5" /> Campo / ubicación</label>
          <select data-testid="cal-form-venue" value={f.venue_id} onChange={(e) => set("venue_id", e.target.value)} className={inputCls}>
            <option value="">— Sin asignar —</option>
            {(data.venues || []).map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </div>
        {f.type === "partido" && (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="overline block mb-1">Rival</label>
                <input data-testid="cal-form-opponent" type="text" value={f.opponent} onChange={(e) => set("opponent", e.target.value)} placeholder="CD Rival" className={inputCls} />
              </div>
              <div>
                <label className="overline block mb-1">Local/Visitante</label>
                <select data-testid="cal-form-homeaway" value={f.home_away} onChange={(e) => set("home_away", e.target.value)} className={inputCls}>
                  <option value="casa">En casa</option>
                  <option value="fuera">Fuera</option>
                </select>
              </div>
            </div>
            <div>
              <label className="overline block mb-1">Convocatoria ({f.convocados.length} seleccionados)</label>
              <div className="border border-border max-h-44 overflow-y-auto divide-y divide-border/40" data-testid="cal-convocatoria">
                {teamPlayers.map(p => (
                  <label key={p.id} className="flex items-center gap-2 p-2 cursor-pointer hover:bg-white/[0.02]">
                    <input type="checkbox" data-testid={`cal-conv-${p.id}`} checked={f.convocados.includes(p.id)} onChange={() => toggleConv(p.id)} className="accent-[#dc2626]" />
                    <span className="font-heading font-bold text-xs uppercase">{p.name} {p.jersey_number ? <span className="opacity-60">#{p.jersey_number}</span> : null}</span>
                  </label>
                ))}
                {teamPlayers.length === 0 && <div className="p-3 text-xs text-muted-foreground">Este equipo no tiene jugadores.</div>}
              </div>
            </div>
          </>
        )}
        <div>
          <label className="overline block mb-1">Notas</label>
          <textarea data-testid="cal-form-notes" value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="w-full bg-transparent border border-border p-3 font-mono text-sm focus:border-primary focus:outline-none" />
        </div>
        {event.announcement_id ? (
          <div className="text-[11px] text-emerald-500 font-mono" data-testid="cal-conv-sent">✓ Convocatoria ya enviada a las familias</div>
        ) : (
          <label className="flex items-center gap-2 cursor-pointer border border-primary/40 p-3 hover:bg-primary/5">
            <input type="checkbox" data-testid="cal-notify-families" checked={!!f.notify_families} onChange={(e) => set("notify_families", e.target.checked)} className="accent-[#dc2626]" />
            <span className="text-xs font-heading font-bold uppercase">📧 Enviar convocatoria a las familias <span className="text-muted-foreground normal-case font-mono">(email con botones Sí/No asistirá)</span></span>
          </label>
        )}
        <div className="flex justify-end gap-3 pt-1">
          <button onClick={onClose} className="h-10 px-5 border border-border text-sm uppercase font-heading tracking-wider">Cancelar</button>
          <button data-testid="cal-form-save" onClick={() => save(false)} disabled={saving} className="btn-volt disabled:opacity-50">{saving ? "Guardando…" : "Guardar"}</button>
        </div>
      </div>
    </div>
  );
}

function EventRsvps({ eventId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api.get(`/calendar/events/${eventId}/rsvps`).then(({ data }) => setData(data)).catch(() => {});
  }, [eventId]);
  if (!data || !data.sent) return null;
  return (
    <div className="mt-2" data-testid={`cal-rsvps-${eventId}`}>
      <button type="button" data-testid={`cal-rsvps-names-${eventId}`} onClick={() => setOpen(v => !v)}
              className="text-[11px] font-mono border border-border px-2 py-1 hover:border-primary inline-flex items-center gap-2">
        <span className="text-emerald-500">✅ {data.yes.length}</span>
        <span className="text-red-500">❌ {data.no.length}</span>
        <span className="text-amber-400">⏳ {data.pending.length}</span>
        <span className="text-muted-foreground">{open ? "ocultar" : "ver nombres"}</span>
      </button>
      {open && (
        <div className="mt-1.5 border border-border p-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px] font-mono">
          <div><span className="text-emerald-500 font-bold">✅ Confirmados</span>{data.yes.length === 0 ? <div className="text-muted-foreground">—</div> : data.yes.map(n => <div key={n}>{n}</div>)}</div>
          <div><span className="text-red-500 font-bold">❌ No asisten</span>{data.no.length === 0 ? <div className="text-muted-foreground">—</div> : data.no.map(n => <div key={n}>{n}</div>)}</div>
          <div><span className="text-amber-400 font-bold">⏳ Sin responder</span>{data.pending.length === 0 ? <div className="text-muted-foreground">—</div> : data.pending.map(n => <div key={n}>{n}</div>)}</div>
        </div>
      )}
    </div>
  );
}

function ConfigPanel({ data, user, reload }) {
  const isAdmin = ["club_admin", "super_admin"].includes(user?.role);
  const isManager = ["club_admin", "super_admin", "coordinator", "director_deportivo"].includes(user?.role);
  return (
    <div className="border-2 border-primary/40 p-5 mb-6 space-y-6" data-testid="cal-config-panel">
      {isManager && <VenuesConfig venues={data.venues} reload={reload} />}
      <TeamsConfig data={data} reload={reload} />
      {isAdmin && <CoachPermission value={data.coaches_can_manage_calendar} reload={reload} />}
    </div>
  );
}

function VenuesConfig({ venues, reload }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("mixto");
  const add = async () => {
    if (name.trim().length < 2) { toast.error("Nombre demasiado corto"); return; }
    try { await api.post("/venues", { name, type }); setName(""); toast.success("Campo añadido"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("¿Eliminar este campo?")) return;
    try { await api.delete(`/venues/${id}`); reload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const TYPE_LABEL = { entreno: "Entreno", partido: "Partido", mixto: "Mixto" };
  return (
    <div>
      <div className="overline mb-2 flex items-center gap-2"><MapPin size={12} /> Campos del club</div>
      <div className="flex gap-2 flex-wrap mb-3">
        <input data-testid="venue-name-input" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Campo 1 · Césped artificial" className="h-10 flex-1 min-w-[200px] bg-transparent border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none" />
        <select data-testid="venue-type-select" value={type} onChange={(e) => setType(e.target.value)} className="h-10 bg-card border border-border px-2 font-mono text-xs">
          <option value="mixto">Mixto</option>
          <option value="entreno">Solo entreno</option>
          <option value="partido">Solo partido</option>
        </select>
        <button data-testid="venue-add-btn" onClick={add} className="btn-volt h-10"><Plus size={14} /> Añadir</button>
      </div>
      <div className="flex gap-2 flex-wrap">
        {venues.map(v => (
          <span key={v.id} data-testid={`venue-chip-${v.id}`} className="inline-flex items-center gap-2 border border-border px-3 py-1.5 text-xs font-mono">
            <MapPin size={11} className="text-primary" /> {v.name} <span className="text-muted-foreground text-[9px] uppercase">{TYPE_LABEL[v.type] || v.type}</span>
            <button data-testid={`venue-del-${v.id}`} onClick={() => del(v.id)} className="text-destructive hover:opacity-70"><X size={11} /></button>
          </span>
        ))}
        {venues.length === 0 && <span className="text-xs text-muted-foreground">Sin campos aún — añade el primero.</span>}
      </div>
    </div>
  );
}

function TeamsConfig({ data, reload }) {
  const manageable = (data.teams || []).filter(t => (data.manageable_team_ids || []).includes(t.id));
  if (!data.can_manage || manageable.length === 0) return null;
  return (
    <div>
      <div className="overline mb-2">Color y horario semanal por equipo</div>
      <div className="space-y-3">
        {manageable.map(t => <TeamScheduleRow key={t.id} team={t} colors={data.team_colors} venues={data.venues} reload={reload} />)}
      </div>
    </div>
  );
}

function TeamScheduleRow({ team, colors, venues, reload }) {
  const [open, setOpen] = useState(false);
  const [color, setColor] = useState(team.color);
  const [slots, setSlots] = useState(team.training_schedule || []);
  const [until, setUntil] = useState(team.training_until || "");
  const [saving, setSaving] = useState(false);

  const save = async (newColor, newSlots, newUntil) => {
    setSaving(true);
    try {
      await api.put(`/teams/${team.id}/calendar-settings`, {
        color: newColor ?? color, training_schedule: newSlots ?? slots,
        training_until: (newUntil !== undefined ? newUntil : until) || "",
      });
      toast.success(`${team.name} actualizado`);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-border" data-testid={`team-cal-row-${team.id}`}>
      <button onClick={() => setOpen(o => !o)} className="w-full p-3 flex items-center gap-3 hover:bg-white/[0.02]">
        <span className="w-4 h-4 inline-block flex-shrink-0" style={{ background: color }} />
        <span className="font-heading font-bold text-sm uppercase flex-1 text-left">{team.name}</span>
        <span className="text-[10px] font-mono text-muted-foreground">
          {slots.length ? slots.map(s => `${WD_SHORT[s.weekday]}${s.time ? ` ${s.time}` : ""}`).join(" · ") : "Sin horario"}
        </span>
      </button>
      {open && (
        <div className="p-3 border-t border-border space-y-3">
          <div>
            <div className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-1.5">Color del equipo</div>
            <div className="flex gap-1.5 flex-wrap">
              {colors.map(c => (
                <button key={c} data-testid={`team-color-${team.id}-${c.slice(1)}`} onClick={() => { setColor(c); save(c, undefined); }} className={`w-7 h-7 border-2 ${color === c ? "border-white scale-110" : "border-transparent"}`} style={{ background: c }} />
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-1.5">Repetir horario hasta (vacío = sin límite)</div>
            <div className="flex gap-2 items-center">
              <input data-testid={`team-until-${team.id}`} type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="h-9 bg-transparent border border-border px-2 font-mono text-xs" />
              <button disabled={saving} onClick={() => save(undefined, undefined, until)} className="h-9 px-3 border border-border hover:border-primary text-[10px] font-heading uppercase tracking-widest">Guardar</button>
              {until && <button disabled={saving} onClick={() => { setUntil(""); save(undefined, undefined, ""); }} className="h-9 px-3 border border-border hover:border-destructive text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Quitar límite</button>}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">A partir de esa fecha los entrenos ya no se repiten solos: usa "Copiar semana" para planificar por semanas o meses.</p>
          </div>
          <div>
            <div className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-1.5">Días de entreno</div>
            <div className="space-y-2">
              {slots.map((s, i) => (
                <div key={i} className="flex gap-2 items-center flex-wrap">
                  <select value={s.weekday} onChange={(e) => setSlots(sl => sl.map((x, j) => j === i ? { ...x, weekday: Number(e.target.value) } : x))} className="h-9 bg-card border border-border px-2 font-mono text-xs">
                    {WEEKDAYS.map((d, wi) => <option key={wi} value={wi}>{d}</option>)}
                  </select>
                  <input type="time" value={s.time || ""} onChange={(e) => setSlots(sl => sl.map((x, j) => j === i ? { ...x, time: e.target.value } : x))} className="h-9 bg-transparent border border-border px-2 font-mono text-xs" />
                  <select value={s.venue_id || ""} onChange={(e) => setSlots(sl => sl.map((x, j) => j === i ? { ...x, venue_id: e.target.value || null } : x))} className="h-9 bg-card border border-border px-2 font-mono text-xs">
                    <option value="">Sin campo</option>
                    {venues.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                  <button data-testid={`slot-del-${team.id}-${i}`} onClick={() => setSlots(sl => sl.filter((_, j) => j !== i))} className="p-1.5 border border-destructive/50 text-destructive"><Trash2 size={12} /></button>
                </div>
              ))}
              <div className="flex gap-2">
                <button data-testid={`slot-add-${team.id}`} onClick={() => setSlots(sl => [...sl, { weekday: 1, time: "18:00", venue_id: null }])} className="h-9 px-3 border border-border hover:border-primary text-[10px] font-heading uppercase tracking-widest inline-flex items-center gap-1"><Plus size={12} /> Añadir día</button>
                <button data-testid={`slot-save-${team.id}`} onClick={() => save(undefined, slots)} disabled={saving} className="btn-volt h-9 disabled:opacity-50">{saving ? "…" : "Guardar horario"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CoachPermission({ value, reload }) {
  const toggle = async () => {
    try { await api.put("/clubs/calendar-permissions", { coaches_can_manage_calendar: !value }); toast.success(!value ? "Entrenadores pueden gestionar el calendario" : "Permiso retirado a entrenadores"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="flex items-center justify-between border border-border p-3">
      <div>
        <div className="font-heading font-bold text-sm uppercase">Permitir a los entrenadores gestionar el calendario</div>
        <div className="text-xs text-muted-foreground mt-0.5">Si está activo, cada entrenador puede crear y editar entrenos, horarios y partidos de SUS equipos.</div>
      </div>
      <button data-testid="coach-permission-toggle" onClick={toggle} className={`h-8 w-14 border-2 flex-shrink-0 relative transition-colors ${value ? "border-emerald-500 bg-emerald-500/20" : "border-border"}`}>
        <span className={`absolute top-0.5 h-5 w-5 transition-all ${value ? "right-0.5 bg-emerald-400" : "left-0.5 bg-muted-foreground"}`} />
      </button>
    </div>
  );
}
